import { Box, Tooltip, Typography } from '@mui/material'
import CheckIcon from '@mui/icons-material/Check'
import { COLORS } from './theme'
import { SEGMENTS, STEPS, TRANSITION_STEPS } from './onboardingSteps'
import { useOnboardingV2 } from './OnboardingContext'

type SegmentState = 'completed' | 'current' | 'ahead-visited' | 'future'

function getSegmentState(
  segmentLabel: string,
  bannerSegment: string,
  currentStep: number,
  startStep: number,
  endStep: number,
  maxStepReached: number,
): SegmentState {
  if (currentStep >= STEPS.DRAFT_COMPLETE) return 'completed'
  if (segmentLabel.toLowerCase() === bannerSegment) return 'current'
  if (currentStep > endStep) return 'completed'
  if (maxStepReached > endStep) return 'completed'
  if (maxStepReached >= startStep && startStep > currentStep) return 'ahead-visited'
  return 'future'
}

/** Target step for completed (behind) segments. */
function getCompletedTarget(
  label: string,
  startStep: number,
  scanResultId: string | null,
  applicationId: string | null,
): number {
  switch (label.toLowerCase()) {
    case 'intro': return STEPS.INTRO_VIDEO
    case 'organisation': return STEPS.ORG_DETAILS
    case 'location': return STEPS.ORG_LOCATIONS
    // The segment's first step, not a named one: a named step goes stale when
    // the upload steps are reordered, and the banner then skips whichever step
    // moved ahead of it.
    case 'upload': return startStep
    case 'profile': return STEPS.PROFILE_REVIEW
    case 'funding': return STEPS.FUNDING_DETAILS
    case 'scan':
      return scanResultId ? STEPS.SCAN_RESULTS : STEPS.FUNDING_DETAILS
    case 'draft':
      if (applicationId) return STEPS.DRAFT_COMPLETE
      if (scanResultId) return STEPS.SCAN_RESULTS
      return STEPS.FUNDING_DETAILS
    default: return STEPS.INTRO_VIDEO
  }
}

/** Target step for ahead-visited segments: last step reached within segment, skipping transitions. */
function getAheadVisitedTarget(
  startStep: number,
  endStep: number,
  maxStepReached: number,
): number {
  let target = Math.min(maxStepReached, endStep)
  while (target > startStep && TRANSITION_STEPS.has(target)) {
    target--
  }
  return target
}

export default function OnboardingStepperBanner() {
  const { currentStep, bannerSegment, maxStepReached, goToStep, scanResultId, applicationId } =
    useOnboardingV2()

  return (
    <Box
      sx={{
        display: 'flex',
        width: '100%',
        gap: 0.5,
        px: 3,
        py: 2,
      }}
    >
      {SEGMENTS.map((segment) => {
        const state = getSegmentState(
          segment.label,
          bannerSegment,
          currentStep,
          segment.startStep,
          segment.endStep,
          maxStepReached,
        )

        const isClickable =
          (state === 'completed' || state === 'ahead-visited') &&
          currentStep < STEPS.DRAFT_COMPLETE

        let targetStep = segment.startStep
        if (isClickable) {
          if (state === 'completed') {
            targetStep = getCompletedTarget(
              segment.label,
              segment.startStep,
              scanResultId,
              applicationId,
            )
          } else {
            targetStep = getAheadVisitedTarget(
              segment.startStep,
              segment.endStep,
              maxStepReached,
            )
          }
        }

        const segmentContent = (
          <Box
            key={segment.label}
            role={isClickable ? 'button' : undefined}
            tabIndex={isClickable ? 0 : undefined}
            onClick={isClickable ? () => goToStep(targetStep) : undefined}
            onKeyDown={
              isClickable
                ? (e: React.KeyboardEvent) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault()
                      goToStep(targetStep)
                    }
                  }
                : undefined
            }
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 0.5,
              cursor: isClickable ? 'pointer' : 'default',
              borderRadius: 1,
              px: 0.5,
              py: 0.5,
              transition: 'background-color 0.2s ease',
              ...(isClickable && {
                '&:hover': {
                  backgroundColor: 'rgba(0,0,0,0.04)',
                },
                '&:focus-visible': {
                  outline: `2px solid ${COLORS.primary}`,
                  outlineOffset: 2,
                },
              }),
            }}
          >
            <Box
              sx={{
                width: '100%',
                height: 8,
                borderRadius: 4,
                backgroundColor:
                  state === 'completed' || state === 'ahead-visited'
                    ? COLORS.primary
                    : state === 'current'
                      ? COLORS.primary
                      : COLORS.border,
                opacity: state === 'current' ? 0.7 : state === 'ahead-visited' ? 0.5 : 1,
                transition: 'background-color 0.3s ease',
              }}
            />
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                minHeight: 20,
              }}
            >
              {state === 'completed' && (
                <CheckIcon sx={{ fontSize: 14, color: COLORS.primary }} />
              )}
              <Typography
                variant="caption"
                sx={{
                  color: state === 'future' ? COLORS.lightGray : COLORS.primary,
                  fontWeight: state === 'current' ? 600 : 400,
                  fontSize: '0.7rem',
                }}
              >
                {segment.label}
              </Typography>
            </Box>
          </Box>
        )

        if (isClickable) {
          return (
            <Tooltip key={segment.label} title="Click to review" arrow>
              {segmentContent}
            </Tooltip>
          )
        }

        return segmentContent
      })}
    </Box>
  )
}
